import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { GoMaterialModule } from '@go-modules/go-material.module';
import {
	EventService,
	GoEvent
} from '@go-modules/services/event/event.service';
import { BehaviorSubject, combineLatest, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { AIMarkersDialogComponent } from '@go-modules/components/dialogs/ai-markers-dialog/ai-markers-dialog.component';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import { CommentViewerModule } from '../comment-viewer/comment-viewer.module';
import { Marker } from '@go-modules/interfaces/markers/marker';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { PlayerSync } from '@go-modules/interfaces/media/media';
import { CommentTabHeaderComponent } from '../comment-tab-header/comment-tab-header.component';
import { CommentPanelBannersComponent } from './comment-panel-banners/comment-panel-banners.component';
import { NgxCommentService } from '@go-modules/services/comment/comment.service';
import { CommentBannersService } from '@go-modules/services/comment-banners/comment-banners.service';

export enum FeedbackDisplayZeroState {
	ADD_AI_MARKERS_COMMENT = 'add-ai-markers-comment',
	ADD_COMMENTS = 'add-comments',
	NO_FEEDBACK_SHOWN = 'no-feedback-shown'
}

@Component({
	selector: 'comment-panel',
	templateUrl: './comment-panel.component.html',
	styleUrls: ['./comment-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		GoMaterialModule,
		NgxZeroStateModule,
		CommentViewerModule,
		CommentTabHeaderComponent,
		CommentPanelBannersComponent
	]
})
export class CommentPanelComponent implements OnInit, OnDestroy {
	@Input() public feedbackSession: any;
	@Input() public playerSync: PlayerSync;
	@Input() public viewFrom: string = 'comment';
	@Input() public tags: Marker[] = [];
	public zeroState: FeedbackDisplayZeroState | null = null;
	public zeroStates = FeedbackDisplayZeroState;
	public rerunningAiFeedback: boolean = false;
	public feedbackLoaded$ = new BehaviorSubject<boolean>(false);
	private eventSubscription: any;
	private eventNames: string[];
	private componentDestroyed$$ = new Subject<void>();
	private reloadComments$ = new BehaviorSubject<boolean>(false);
	private isReplyingOrUpdatingComment$ = new BehaviorSubject<boolean>(false);
	private environmentVarsService: EnvironmentVarsService;

	constructor (
		public ngxGoToastService: NgxGoToastService,
		private eventService: EventService,
		private ngxGoModalService: GoModalService,
		private ngxCommentListService: CommentListService,
		private sessionService: NgxSessionService,
		private cdr: ChangeDetectorRef,
		private ngxCommentService: NgxCommentService,
		private commentBannersService: CommentBannersService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(selectedServiceToken) private selectedService: SelectedService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnInit () {
		this.eventNames = [
			EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_COMPLETED,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_FAILED
		];
		this.eventSubscription = this.eventService.events
			.pipe(filter((ev: GoEvent) => this.eventNames.includes(ev.name)))
			.subscribe((ev: GoEvent) => {
				switch (ev.name) {
					case EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED:
						this.feedbackLoaded$.next(true);
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_COMPLETED:
						this.reloadComments$.next(true);
						this.rerunningAiFeedback = false;
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_FAILED:
						this.rerunningAiFeedback = false;

						if (this.showReRunAiFeedbackButton()) {
							this.ngxGoToastService.createToast({
								type: GoToastStatusType.ERROR,
								message: 'feedback-session-ai-feedback-failed',
								actionLink: {
									title: 'common_try-again',
									action: () => this.rerunAiFeedback()
								}
							});
						} else {
							this.ngxGoToastService.createToast({
								type: GoToastStatusType.ERROR,
								message: 'feedback-session-ai-feedback-failed'
							});
						}
						break;
				}
			});

		combineLatest([this.reloadComments$, this.isReplyingOrUpdatingComment$])
			.pipe(
				takeUntil(this.componentDestroyed$$),
				filter(([reload, isReplyingOrUpdating]) => reload && !isReplyingOrUpdating),
				switchMap(() => {
					const session = this.selectedService.getSession();
					return this.ngxCommentService.getForSession(session.session_id);
				})
			)
			.subscribe((comments) => {
				const oldNumComments = this.ngxCommentListService.currentComments.length;
				this.ngxCommentListService.setComments(comments);
				const currentNumComments = comments.length;
				const newNumComments = currentNumComments - oldNumComments;
				this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENTS_LOADED, newNumComments);
				this.reloadComments$.next(false);
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'feedback-session-ai-feedback-success'
				});

			});

		combineLatest([
			this.ngxCommentListService.comments$,
			this.feedbackLoaded$
		]).pipe(
			takeUntil(this.componentDestroyed$$)
		).subscribe(([comments, _feedbackLoaded]) => {
			this.checkZeroState(comments);
		});

		const savedFeedbackSettings = localStorage.getItem('feedbackSettings');
		if (savedFeedbackSettings) {
			const feedbackSettings = JSON.parse(savedFeedbackSettings) || {};
			if ('filterOutSyncEventComments' in feedbackSettings) {
				this.ngxCommentListService.toggleFilterOutSyncEventComments(
					feedbackSettings.filterOutSyncEventComments
				);
			}
		}
	}

	public ngOnDestroy () {
		this.eventSubscription.unsubscribe();
		this.componentDestroyed$$.next();
		this.componentDestroyed$$.complete();
	}

	public rerunAiFeedback = () => {
		this.rerunningAiFeedback = true;

		const session = this.selectedService.getSession();

		this.sessionService.generateAiFeedback(session.session_id)
			.subscribe({
				next: () => {
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.SUCCESS,
						message: 'feedback-session-rerun-ai-feedback-success-message'
					});

					this.commentBannersService.showGeneratingAiFeedbackBanner();
				},
				error: () => {
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.ERROR,
						message: 'feedback-session-rerun-ai-feedback-error-message'
					});
				}
			});
	};

	public addAiAssistant () {
		const activity = this.selectedService.getActivity();
		const previousDefinitions = structuredClone(activity.llm_definitions);

		return this.ngxGoModalService
			.open(AIMarkersDialogComponent, true, {
				data: {
					activity,
					llmDefinitions: activity.llm_definitions,
					aiTranscriptionLanguage: activity.ai_transcription_language
				}
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					activity.ai_transcription_language =
						result.ai_transcription_language;

					const newDefinitions = structuredClone(result.llm_definitions);

					if (this.addedOrRemovedAiMarkers(previousDefinitions, newDefinitions)) {
						this.commentBannersService.showGeneratingAiFeedbackBanner();
					}

					activity.llm_definitions = result.llm_definitions;
					this.selectedService.setActivity(activity);

					activity.save();
				}
			});
	}

	/**
	 * Whether the Re-run AI Feedback button should be shown
	 *
	 * @returns {boolean}
	 */
	public showReRunAiFeedbackButton = () => {
		const activity = this.selectedService.getActivity();
		const session = this.selectedService.getSession();

		return this.userService.currentUser.is_root_user &&
				activity.ai_prompts_enabled &&
				!session.media.isYoutube();
	};

	public checkZeroState (comments) {
		const previousZeroState = this.zeroState;
		if (comments.length || !this.feedbackLoaded$.getValue()) {
			this.zeroState = null;
		} else {
			if (this.ngxCommentListService.currentComments.length) {
				this.zeroState = FeedbackDisplayZeroState.NO_FEEDBACK_SHOWN;
			} else {
				const group = this.selectedService.getGroup();
				const activity = this.selectedService.getActivity();
				const license = this.selectedService.getLicense();
				const session = this.selectedService.getSession();

				if (
					license?.salesforce_license
						.ai_prompts_enabled &&
					activity.ai_prompts_enabled &&
					group.hasInstructorRole(true) &&
					session.media &&
					!session.media.isYoutube() &&
					(!activity.llm_definitions ||
						activity.llm_definitions.length === 0) &&
					!(activity.parent_child_sync && activity.is_parent_sync_enabled) &&
					!this.environmentVarsService.get(
						EnvironmentVarsService.VAR.DISABLE_ACTIVITY_EDITING
					)
				) {
					this.zeroState = FeedbackDisplayZeroState.ADD_AI_MARKERS_COMMENT;
				} else {
					this.zeroState = FeedbackDisplayZeroState.ADD_COMMENTS;
				}
			}
		}
		// TODO change to observable
		if (previousZeroState !== this.zeroState) {
			this.cdr.detectChanges();
		}
	}

	public toggleIsEdittingCommentState (value: boolean) {
		this.isReplyingOrUpdatingComment$.next(value);
	}

	private addedOrRemovedAiMarkers (previousDefinitions, newDefinitions): boolean {
		const prevDefIds = new Set(previousDefinitions.map((prevDef) => prevDef.id));
		const newDefIds = new Set(newDefinitions.map((newDef) => newDef.id));

		return newDefinitions.some((newDef) => !prevDefIds.has(newDef.id)) ||
			previousDefinitions.some((prevDef) => !newDefIds.has(prevDef.id));
	}
}
